import {
  ActionButtons,
  DataTable,
  DescriptionList,
  InputField,
  Modal,
  Select,
  type DataTableColumn,
  type DataTableExpandedTableConfig,
  type DataTableRow,
} from '@enamelai/design-system'
import { BillingProcedure, LedgerEntry } from '@dataTypes/billing.types'
import { SettingStatus } from '@dataTypes/settingStatus.type'
import React, { useEffect, useMemo, useState } from 'react'
import { formatCurrency, readSelectValue } from '@utils/billing.utils'
import { Col, Row } from 'react-bootstrap'
import useCreateInvoiceModal from './useCreateInvoiceModal.hook'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import './CreateInvoiceModal.css'

interface Props {
  isVisible: boolean
  onClose: () => void
  ledgerEntries?: LedgerEntry[]
  initialSelectedEntry?: LedgerEntry | null
}

const displayOptionalCurrency = (value: number | null): string =>
  value !== null ? formatCurrency(value) : '-'

const procedureAdjustment = (procedure: BillingProcedure): number | null => {
  if (
    procedure.patientAdjustment === null &&
    procedure.insuranceAdjustment === null
  ) {
    return null
  }

  return (
    (procedure.patientAdjustment ?? 0) + (procedure.insuranceAdjustment ?? 0)
  )
}

const CreateInvoiceModal: React.FC<Props> = ({
  isVisible,
  onClose,
  ledgerEntries = [],
  initialSelectedEntry = null,
}) => {
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([])

  const hooks = useCreateInvoiceModal(
    isVisible,
    onClose,
    ledgerEntries,
    initialSelectedEntry,
    showLoading,
    hideLoading,
    showToast,
  )

  useEffect(() => {
    if (!isVisible) {
      setExpandedRowIds([])
      setIsSubmitting(false)
    }
  }, [isVisible])

  const entriesById = useMemo(() => {
    const entries = new Map<string, LedgerEntry>()
    hooks.selectedLedgerEntries.forEach((entry) => {
      entries.set(entry.appointmentId, entry)
    })
    return entries
  }, [hooks.selectedLedgerEntries])

  const rows = useMemo(
    (): DataTableRow[] =>
      hooks.selectedLedgerEntries.map((entry) => ({
        id: entry.appointmentId,
      })),
    [hooks.selectedLedgerEntries],
  )

  const columns = useMemo((): DataTableColumn[] => {
    const discountPercent = hooks.discountPercent

    return [
      {
        id: 'appointmentType',
        label: 'Appointment',
        renderCell: (row) => {
          const entry = entriesById.get(row.id)
          if (!entry) return null
          return (
            <div className="d-flex flex-column">
              <span className="fw-bold">{entry.appointmentType}</span>
              <small className="text-muted">{entry.appointmentDate}</small>
            </div>
          )
        },
      },
      {
        id: 'providerName',
        label: 'Provider name',
        renderCell: (row) => entriesById.get(row.id)?.providerName ?? null,
      },
      {
        id: 'clinicFee',
        label: 'Charges',
        renderCell: (row) => {
          const entry = entriesById.get(row.id)
          return entry ? formatCurrency(entry.clinicFee) : null
        },
      },
      {
        id: 'approvedInsurance',
        label: 'Approved ins.',
        renderCell: (row) => {
          const entry = entriesById.get(row.id)
          return entry ? displayOptionalCurrency(entry.approvedInsurance) : null
        },
      },
      {
        id: 'discount',
        label: 'Discount',
        renderCell: (row) => {
          const entry = entriesById.get(row.id)
          if (!entry) return null
          return formatCurrency(
            (entry.patientResponsibility * discountPercent) / 100,
          )
        },
      },
      {
        id: 'appointmentAdjustment',
        label: 'Appointment adj.',
        renderCell: (row) => {
          const entry = entriesById.get(row.id)
          return entry
            ? displayOptionalCurrency(entry.appointmentAdjustment)
            : null
        },
      },
      {
        id: 'patientBalance',
        label: 'Patient bal.',
        renderCell: (row) => {
          const entry = entriesById.get(row.id)
          if (!entry) return null
          const discount = (entry.patientResponsibility * discountPercent) / 100
          const adjustment = entry.appointmentAdjustment ?? 0
          return formatCurrency(
            entry.patientResponsibility - discount - adjustment,
          )
        },
      },
    ]
  }, [entriesById, hooks.discountPercent])

  const expandedTable = useMemo((): DataTableExpandedTableConfig => {
    const discountPercent = hooks.discountPercent

    return {
      columns: [
        {
          id: 'description',
          label: 'Procedure',
          colSpan: 2,
          renderCell: (row) => row.description,
        },
        {
          id: 'clinicFee',
          label: 'Charges',
          renderCell: (row) => row.clinicFee,
        },
        {
          id: 'approvedInsurance',
          label: 'Approved ins.',
          renderCell: (row) => row.approvedInsurance,
        },
        {
          id: 'discount',
          label: 'Discount',
          renderCell: (row) => row.discount,
        },
        {
          id: 'appointmentAdjustment',
          label: 'Appointment adj.',
          renderCell: (row) => row.appointmentAdjustment,
        },
        {
          id: 'patientBalance',
          label: 'Patient bal.',
          renderCell: (row) => row.patientBalance,
        },
      ],
      getRows: (parentRow) => {
        const entry = entriesById.get(parentRow.id)
        if (!entry) return []

        return entry.procedures.map((procedure, index) => {
          const discount = (procedure.clinicFee * discountPercent) / 100
          const adjustment = procedureAdjustment(procedure)

          return {
            id:
              procedure.procedureLogId ||
              `${entry.appointmentId}-procedure-${index}`,
            description: (
              <div className="d-flex flex-column">
                <span>{procedure.description}</span>
                <small className="text-muted">{procedure.procedureCode}</small>
              </div>
            ),
            clinicFee: formatCurrency(procedure.clinicFee),
            approvedInsurance: displayOptionalCurrency(
              procedure.approvedInsurance,
            ),
            discount: formatCurrency(discount),
            appointmentAdjustment: displayOptionalCurrency(adjustment),
            patientBalance: formatCurrency(
              procedure.clinicFee - discount - (adjustment ?? 0),
            ),
          }
        })
      },
    }
  }, [entriesById, hooks.discountPercent])

  const discountOptions = useMemo(() => {
    const activeDiscounts = hooks.discounts.filter(
      (discount) => discount.status !== SettingStatus.Disabled,
    )

    if (activeDiscounts.length === 0) {
      return [
        {
          value: '-1',
          label: 'No discount settings - Enter custom',
          trailingText: '%',
        },
      ]
    }

    return [
      ...activeDiscounts.map((discount) => ({
        value: discount.discount_id,
        label: discount.description,
        trailingText: `${discount.discount_percentage}%`,
      })),
      {
        value: '-1',
        label: 'Custom',
        trailingText: '%',
      },
    ]
  }, [hooks.discounts])

  const handlePrimaryClick = async (): Promise<void> => {
    setIsSubmitting(true)
    try {
      await hooks.handleCreateInvoice()
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderActions = (row: DataTableRow): React.ReactNode => (
    <ActionButtons onDelete={() => hooks.handleDelete(row.id)} />
  )

  return (
    <Modal
      title="Create invoice"
      open={isVisible}
      onClose={onClose}
      size="xl"
      style={{ maxWidth: 'min(1280px, calc(100vw - 48px))' }}
      closeOnBackdrop={false}
      primaryLabel="Create invoice"
      secondaryLabel="Cancel"
      primaryDisabled={isSubmitting}
      onPrimaryClick={handlePrimaryClick}
      onSecondaryClick={onClose}
    >
      <Row className="mb-2 gx-2">
        <Col>
          <Select
            className="create-invoice-modal-field"
            label="Appointment"
            value={hooks.selectedAppointmentKey}
            options={hooks.availableLedgerEntries.map((entry) => ({
              value: entry.appointmentId,
              label: `${entry.appointmentType} - ${entry.appointmentDate} - ${entry.patientName}`,
            }))}
            onChange={(value) => {
              const appointmentId = readSelectValue(value)
              hooks.setSelectedAppointmentKey(appointmentId)
              if (appointmentId) {
                hooks.handleSelectAppointment(appointmentId)
              }
            }}
            placeholder="Select appointment"
          />
        </Col>
        <Col>
          <Select
            className="create-invoice-modal-field"
            label="Discount"
            value={hooks.selectedDiscountId}
            options={discountOptions}
            onChange={(value) => {
              hooks.setSelectedDiscountId(readSelectValue(value))
            }}
            placeholder="Select discount"
          />
        </Col>
        <Col>
          <InputField
            className="create-invoice-modal-field"
            label="Discount (%)"
            variant="number"
            fullWidth
            numberMin={0}
            numberMax={100}
            numberStep={1}
            numberDecimalPlaces={2}
            value={hooks.discountPercent}
            onChange={(event) => {
              const value =
                event.target.value === '' ? 0 : Number(event.target.value)
              const clampedValue = Math.max(0, Math.min(100, value))
              hooks.setCustomDiscount(clampedValue)
            }}
            disabled={hooks.selectedDiscountId !== '-1'}
          />
        </Col>
      </Row>

      <DataTable
        columns={columns}
        rows={rows}
        state={rows.length === 0 ? 'empty' : 'default'}
        emptyState={{
          title: 'No appointments selected.',
          description: '',
        }}
        expandable
        expandableColumnId="appointmentType"
        expandedRowIds={expandedRowIds}
        onExpandedRowIdsChange={setExpandedRowIds}
        expandedTable={expandedTable}
        showActions
        actionsLabel="Actions"
        renderActions={renderActions}
      />

      <DescriptionList
        className="mt-3"
        variant="file-details"
        columns={1}
        items={[
          {
            label: 'Charges',
            value: formatCurrency(hooks.totals.totalCharges),
          },
          {
            label: 'Approved Insurance',
            value: formatCurrency(hooks.totals.totalApprovedInsurance),
          },
          {
            label: 'Discount',
            value: formatCurrency(hooks.totals.totalDiscount),
          },
          {
            label: 'Appointment Adjustment',
            value: formatCurrency(hooks.totals.totalAppointmentAdjustment),
          },
          {
            label: 'Total',
            value: formatCurrency(hooks.totals.totalOutstanding),
          },
        ]}
      />
    </Modal>
  )
}

export default CreateInvoiceModal
